import { useState, useEffect, useCallback } from 'react';
import Button from '../ui/Button';
import workScheduleService, { type DaySchedule, DAY_NAMES } from '../../services/workScheduleService';
import organizationService from '../../services/organization';

interface UserScheduleEditorProps {
  /** Target employee. */
  userId: number;
  /** Display name shown in headings and the save button. */
  userName: string;
  /**
   * Org default clock-grace, used for the "inherit" placeholder. When omitted,
   * the component fetches the org setting itself.
   */
  orgGraceMinutes?: number;
}

/**
 * Per-employee work-schedule override editor. Extracted from
 * WorkScheduleSettings so it can be reused by both the standalone page (which
 * also manages org-level defaults) and the unified Staff Profile page.
 */
export default function UserScheduleEditor({ userId, userName, orgGraceMinutes }: UserScheduleEditorProps) {
  const [userSchedule, setUserSchedule] = useState<DaySchedule[]>([]);
  const [userGrace, setUserGrace] = useState<number | null>(null); // null = inherit org default
  const [effectiveUserGrace, setEffectiveUserGrace] = useState<number>(15);
  const [orgGrace, setOrgGrace] = useState<number>(orgGraceMinutes ?? 15);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Resolve the org default grace for the "inherit" placeholder when not supplied.
  useEffect(() => {
    if (orgGraceMinutes !== undefined) {
      setOrgGrace(orgGraceMinutes);
      return;
    }
    let cancelled = false;
    organizationService.getCurrentOrganization().then(res => {
      if (!cancelled && res.success && res.data) {
        setOrgGrace((res.data as any).clock_grace_minutes ?? 15);
      }
    }).catch(() => { /* defaults are fine */ });
    return () => { cancelled = true; };
  }, [orgGraceMinutes]);

  const loadUserSchedule = useCallback(async (uid: number) => {
    setLoading(true);
    setError(null);
    try {
      const response = await workScheduleService.getUserSchedule(uid);
      if (response.success && response.data) {
        setUserSchedule(response.data);
        setUserGrace(response.clock_grace_minutes ?? null);
        setEffectiveUserGrace(response.effective_clock_grace_minutes ?? 15);
      }
    } catch {
      setError('Failed to load user schedule');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadUserSchedule(userId);
    setSuccess(null);
  }, [userId, loadUserSchedule]);

  const updateUserDay = (dayOfWeek: number, field: keyof DaySchedule, value: string | number | boolean | null) => {
    setUserSchedule(prev => prev.map(d =>
      d.day_of_week === dayOfWeek ? { ...d, [field]: value } : d
    ));
  };

  const clearUserOverride = (dayOfWeek: number) => {
    updateUserDay(dayOfWeek, 'start_time', null);
    updateUserDay(dayOfWeek, 'end_time', null);
    updateUserDay(dayOfWeek, 'lunch_duration_minutes', null);
    updateUserDay(dayOfWeek, 'is_working_day', null);
  };

  const saveUserSchedule = async () => {
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      const response = await workScheduleService.updateUserSchedule(userId, userSchedule, userGrace);
      if (response.success) {
        setEffectiveUserGrace(response.effective_clock_grace_minutes ?? effectiveUserGrace);
        setSuccess(`Schedule for ${userName} saved successfully`);
        setTimeout(() => setSuccess(null), 3000);
      } else {
        setError((response as any).error?.message || 'Failed to save');
      }
    } catch {
      setError('Failed to save user schedule');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <p className="text-sm text-gray-500">Loading schedule for {userName}…</p>;
  }

  return (
    <>
      <p className="text-sm font-medium text-gray-700 mb-3">
        Override schedule for <span className="text-blue-700">{userName}</span>
        <span className="text-xs text-gray-500 ml-2">(leave blank to inherit org default)</span>
      </p>

      <div className="mb-4 p-3 border border-gray-200 rounded-md bg-gray-50 max-w-md">
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Clock Grace Window Override (minutes)
        </label>
        <div className="flex items-center gap-2">
          <input
            type="number"
            min={0}
            max={60}
            placeholder={`Inherit (${orgGrace})`}
            value={userGrace ?? ''}
            onChange={e => {
              const v = e.target.value;
              setUserGrace(v === '' ? null : parseInt(v));
            }}
            className="w-28 border border-gray-300 rounded px-2 py-1 text-sm"
          />
          {userGrace !== null && (
            <button
              type="button"
              onClick={() => setUserGrace(null)}
              className="text-xs text-gray-500 underline hover:text-red-600"
            >
              Clear (inherit)
            </button>
          )}
        </div>
        <p className="text-xs text-gray-500 mt-1">
          Leave blank to inherit the organisation default ({orgGrace} min).
          Currently effective: <strong>{effectiveUserGrace} min</strong>.
        </p>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b">
              <th className="text-left py-2 pr-4 font-medium text-gray-700 w-32">Day</th>
              <th className="text-left py-2 pr-4 font-medium text-gray-700">Working Day</th>
              <th className="text-left py-2 pr-4 font-medium text-gray-700">Start Time</th>
              <th className="text-left py-2 pr-4 font-medium text-gray-700">End Time</th>
              <th className="text-left py-2 pr-4 font-medium text-gray-700">Lunch Break (min)</th>
              <th className="text-left py-2 font-medium text-gray-700">Clear Override</th>
            </tr>
          </thead>
          <tbody>
            {userSchedule.map(day => (
              <tr key={day.day_of_week} className="border-b last:border-0">
                <td className="py-2 pr-4 font-medium text-gray-800">{DAY_NAMES[day.day_of_week]}</td>
                <td className="py-2 pr-4">
                  <select
                    className="border border-gray-300 rounded px-2 py-1 text-sm"
                    value={
                      day.is_working_day === null || day.is_working_day === undefined
                        ? ''
                        : day.is_working_day ? 'true' : 'false'
                    }
                    onChange={e => updateUserDay(
                      day.day_of_week,
                      'is_working_day',
                      e.target.value === '' ? null : e.target.value === 'true'
                    )}
                  >
                    <option value="">Inherit</option>
                    <option value="true">Yes</option>
                    <option value="false">No</option>
                  </select>
                </td>
                <td className="py-2 pr-4">
                  <input
                    type="time"
                    value={day.start_time ?? ''}
                    placeholder="Inherit"
                    onChange={e => updateUserDay(day.day_of_week, 'start_time', e.target.value || null)}
                    className="border border-gray-300 rounded px-2 py-1 text-sm"
                  />
                </td>
                <td className="py-2 pr-4">
                  <input
                    type="time"
                    value={day.end_time ?? ''}
                    placeholder="Inherit"
                    onChange={e => updateUserDay(day.day_of_week, 'end_time', e.target.value || null)}
                    className="border border-gray-300 rounded px-2 py-1 text-sm"
                  />
                </td>
                <td className="py-2 pr-4">
                  <input
                    type="number"
                    min={0}
                    max={120}
                    value={day.lunch_duration_minutes ?? ''}
                    placeholder="Inherit"
                    onChange={e => updateUserDay(
                      day.day_of_week,
                      'lunch_duration_minutes',
                      e.target.value === '' ? null : parseInt(e.target.value)
                    )}
                    className="w-20 border border-gray-300 rounded px-2 py-1 text-sm"
                  />
                </td>
                <td className="py-2">
                  <button
                    onClick={() => clearUserOverride(day.day_of_week)}
                    className="text-xs text-gray-500 underline hover:text-red-600"
                  >
                    Clear
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {error && <p className="mt-3 text-sm text-red-600">{error}</p>}
      {success && <p className="mt-3 text-sm text-green-600">{success}</p>}
      <div className="mt-4">
        <Button onClick={saveUserSchedule} disabled={saving}>
          {saving ? 'Saving…' : `Save ${userName}'s Schedule`}
        </Button>
      </div>
    </>
  );
}
